import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Checkbox, Divider, ListItemText, Menu, MenuItem, TextField, Typography } from '@pulse/ui';
import React, { useState, type ReactNode } from 'react';

import { searchAdornment } from './SearchField';

export interface FilterPillProps<T> {
  label: string;
  options: readonly T[];
  value: readonly T[];
  onChange: (v: T[]) => void;
  /** Optional icon prepended to the pill's label. Opt-in — omit for the existing plain-text
   *  pill, unchanged for every pre-existing caller. */
  icon?: ReactNode;
  /** Optional per-option leading visual rendered in the dropdown list, before each option's
   *  label (e.g. a person's avatar for a Project Lead/Manager filter). Purely decorative —
   *  doesn't affect selection/search/Select-All semantics, which are keyed on `getOptionKey`. */
  getOptionIcon?: (option: T) => ReactNode;
  /** Display text for an option, and what the search box matches against. Defaults to
   *  `String(option)` — the identity behavior every pre-existing plain-`string[]` caller relied
   *  on, so this default keeps them byte-identical without passing anything. Override for an
   *  object `T` (e.g. `(u) => u.name)`). */
  getOptionLabel?: (option: T) => string;
  /**
   * Stable identity used for selection/dedup/React `key` — deliberately separate from
   * `getOptionLabel`. Defaults to `getOptionLabel` (i.e. the option's own display text), which
   * is correct only when every option's label is already unique (true for every enum-like
   * `string[]` caller — Status, Priority, Tier, ...). Pass a real id (`(u) => u.id`) whenever
   * `T` is a business entity whose display name isn't guaranteed unique (a user, module, sprint,
   * ...) — two entities sharing a name otherwise collide under a name-only key: React warns
   * "Encountered two children with the same key" and, worse, its list reconciliation can then
   * leave a stale/wrong row rendered after the *other* item's name is filtered in or out (this is
   * exactly how a real duplicate-named user made an unrelated row survive a search that didn't
   * match it). `MultiSelectPicker`/`EntityPicker` don't have this failure mode because they
   * already key by `option.id`; this prop brings `FilterPill` in line with that for the same
   * kind of `T`.
   */
  getOptionKey?: (option: T) => string;
}

// JIRA-style multi-select filter pill. Started byte-identical to AccountsPage.tsx's local
// FilterPill (the original implementation) so that page could be migrated to import this shared
// copy later as a pure one-line swap; getOptionIcon below is a purely additive, opt-in extension
// on top of that same base, so the swap is still zero-change there once it happens.
//
// Generic over `T` (mirroring MultiSelectPicker's own `getOptionLabel`/`getOptionKey` pattern)
// rather than fixed to `string[]` — see `getOptionKey`'s own doc comment for why a label-only
// identity broke down for entity options with non-unique display names. `T` is inferred from
// `options`/`value` at each call site: a caller that still passes plain `string[]`/`string[]`
// gets `T = string` and both accessors default to the identity function, so nothing changes for
// any options that were already unique strings (Status, Priority, Tier, ...).
export function FilterPill<T>({
  label,
  options,
  value,
  onChange,
  icon,
  getOptionIcon,
  getOptionLabel = (option: T) => String(option),
  getOptionKey = getOptionLabel,
}: FilterPillProps<T>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSearch, setMenuSearch] = useState('');
  const open = Boolean(anchorEl);
  const active = value.length > 0;
  const valueKeys = new Set(value.map(getOptionKey));

  const [firstValue] = value;
  const displayLabel = active
    ? value.length === 1 && firstValue !== undefined
      ? `${label}: ${getOptionLabel(firstValue)}`
      : `${label}: ${value.length} selected`
    : label;

  const visibleOptions = menuSearch
    ? options.filter((o) => getOptionLabel(o).toLowerCase().includes(menuSearch.toLowerCase()))
    : options;

  function handleOpen(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
    setMenuSearch('');
  }

  function handleClose() {
    setAnchorEl(null);
    setMenuSearch('');
  }

  function toggle(opt: T) {
    const key = getOptionKey(opt);
    onChange(valueKeys.has(key) ? value.filter((v) => getOptionKey(v) !== key) : [...value, opt]);
  }

  // Scoped to the *visible* options, so with a search term typed "Select All" picks only the
  // matches and leaves selections outside the search untouched — same semantics as
  // MultiSelectPicker's own Select All.
  const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every((o) => valueKeys.has(getOptionKey(o)));
  const someVisibleSelected = visibleOptions.some((o) => valueKeys.has(getOptionKey(o)));

  function toggleSelectAll() {
    if (allVisibleSelected) {
      const visibleKeys = new Set(visibleOptions.map(getOptionKey));
      onChange(value.filter((v) => !visibleKeys.has(getOptionKey(v))));
    } else {
      const existingKeys = new Set(value.map(getOptionKey));
      onChange([...value, ...visibleOptions.filter((o) => !existingKeys.has(getOptionKey(o)))]);
    }
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          height: 32,
          px: 1.5,
          border: 1,
          borderColor: active ? 'primary.main' : 'divider',
          borderRadius: '16px',
          cursor: 'pointer',
          bgcolor: active ? 'primary.50' : 'background.paper',
          color: active ? 'primary.main' : 'text.primary',
          userSelect: 'none',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: active ? 'primary.50' : 'action.hover',
          },
          transition: 'all 0.15s',
        }}
      >
        {icon && (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: 16, color: active ? 'primary.main' : 'text.secondary' }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: active ? 500 : 400,
            lineHeight: 1.5,
            color: 'inherit',
            maxWidth: { xs: 140, sm: 180, md: 220 },
          }}
        >
          {displayLabel}
        </Typography>
        {active ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: 0.5,
              color: 'primary.main',
              borderRadius: '50%',
              '&:hover': { color: 'error.main' },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </Box>
        ) : (
          <ArrowDropDownIcon sx={{ fontSize: 18, ml: 0.25, color: 'text.secondary' }} />
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 220, maxWidth: { xs: 260, sm: 320 }, maxHeight: 380 } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {options.length > 6 && (
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            {/* Not the SearchField wrapper — this one needs onKeyDown/autoFocus handling of its
                own inside a Menu — but it reuses the same adornment so the icon is identical to
                every other search box in the app. */}
            <TextField
              size="small"
              placeholder={`Search ${label}…`}
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{ startAdornment: searchAdornment() }}
              autoFocus
              fullWidth
            />
          </Box>
        )}
        {visibleOptions.length > 0 && (
          <>
            <MenuItem dense onClick={toggleSelectAll} sx={{ alignItems: 'center' }}>
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                sx={{ py: 0 }}
              />
              <ListItemText primary="Select All" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
            </MenuItem>
            <Divider />
          </>
        )}
        {visibleOptions.length === 0 ? (
          <MenuItem disabled dense>
            <Typography variant="body2" color="text.secondary">
              No matches
            </Typography>
          </MenuItem>
        ) : (
          visibleOptions.map((opt) => (
            <MenuItem key={getOptionKey(opt)} dense onClick={() => toggle(opt)} sx={{ alignItems: 'flex-start' }}>
              <Checkbox size="small" checked={valueKeys.has(getOptionKey(opt))} sx={{ py: 0, mt: '2px' }} />
              {getOptionIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, mt: '2px' }}>{getOptionIcon(opt)}</Box>
              )}
              <ListItemText
                primary={getOptionLabel(opt)}
                primaryTypographyProps={{ variant: 'body2', sx: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
